import React, { useState, useEffect, useRef } from 'react';
import { companyProfileAPI, superAdminAPI, settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Upload, Save, Building2, ChevronDown, ToggleLeft, ToggleRight, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const POSTING_MODES = [
  { value: 'manual', label: 'Manual Only', desc: 'All vouchers created manually' },
  { value: 'auto', label: 'Auto-Post', desc: 'Invoice/Receipt auto-creates journal entries' },
  { value: 'hybrid', label: 'Hybrid', desc: 'Auto-draft vouchers, user confirms' },
];

export default function CompanyProfile() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [profile, setProfile] = useState({
    name: '', address: '', gstin: '', phone: '', email: '', cin: '',
    footer_text: '', pdf_color_theme: '#9400D3', posting_mode: 'auto'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gstAutoCalc, setGstAutoCalc] = useState(true);
  const [advanceEngineEnabled, setAdvanceEngineEnabled] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (isSuperAdmin) {
      superAdminAPI.companies({ limit: 100 }).then(r => {
        const comps = r.data?.companies || [];
        setCompanies(comps);
        if (comps.length > 0) setSelectedCompanyId(comps[0].company_id);
      }).catch(() => {});
    }
    companyProfileAPI.get().then(r => {
      setProfile(p => ({ ...p, ...r.data }));
      if (r.data?.logo_url) setLogoUrl(r.data.logo_url);
    }).catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
    api.get('/gst-returns/settings').then(r => setGstAutoCalc(r.data?.auto_generate ?? true)).catch(() => {});
    settingsAPI.getAdvanceEngine().then(r => setAdvanceEngineEnabled(r.data?.enabled !== false)).catch(() => {});
  }, [isSuperAdmin]);

  const loadCompanyProfile = async (companyId) => {
    setSelectedCompanyId(companyId);
    setLoading(true);
    try {
      const r = await companyProfileAPI.get({ company_id: companyId });
      setProfile(p => ({ ...p, ...r.data }));
    } catch { toast.error('Failed to load company profile'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await companyProfileAPI.update(profile);
      setProfile(p => ({ ...p, ...res.data }));
      toast.success('Company profile updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await companyProfileAPI.uploadLogo(file);
      const path = res.data?.path;
      if (path) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${path}?t=${Date.now()}`;
        setLogoUrl(fullUrl);
      }
      toast.success('Logo uploaded successfully');
    } catch { toast.error('Logo upload failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="company-profile-page" className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Company Profile</h1>
            <p className="text-sm text-gray-500">White-label customization — reflects on all PDFs and reports</p>
          </div>
        </div>
      </div>

      {/* Company Selector for Super Admin */}
      {isSuperAdmin && companies.length > 0 && (
        <div className="glass-card rounded-xl p-4" data-testid="company-selector">
          <label className="form-label">Select Company</label>
          <select value={selectedCompanyId} onChange={e => loadCompanyProfile(e.target.value)}
            className="form-input" data-testid="company-select">
            {companies.map(c => (
              <option key={c.company_id} value={c.company_id}>{c.name} ({c.subscription_plan || 'starter'})</option>
            ))}
          </select>
        </div>
      )}

      <div className="glass-card rounded-xl p-6 space-y-5">
        <h3 className="font-heading font-semibold text-slate-800 text-sm border-b border-slate-200 pb-2">Business Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Company Name</label>
            <input type="text" value={profile.name} onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
              className="form-input" data-testid="cp-name" />
          </div>
          <div>
            <label className="form-label">GSTIN</label>
            <input type="text" value={profile.gstin || ''} onChange={(e) => setProfile(p => ({ ...p, gstin: e.target.value }))}
              className="form-input font-mono" data-testid="cp-gstin" />
          </div>
        </div>
        <div>
          <label className="form-label">Address</label>
          <textarea value={profile.address || ''} onChange={(e) => setProfile(p => ({ ...p, address: e.target.value }))}
            className="form-input min-h-[60px] resize-y" data-testid="cp-address" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Phone</label>
            <input type="text" value={profile.phone || ''} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
              className="form-input" data-testid="cp-phone" />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input type="email" value={profile.email || ''} onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
              className="form-input" data-testid="cp-email" />
          </div>
          <div>
            <label className="form-label">CIN</label>
            <input type="text" value={profile.cin || ''} onChange={(e) => setProfile(p => ({ ...p, cin: e.target.value }))}
              className="form-input font-mono" data-testid="cp-cin" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <h3 className="font-heading font-semibold text-slate-800 text-sm border-b border-slate-200 pb-2">Branding & PDF</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Company Logo</label>
            <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="cp-logo-input" />
            <div className="flex items-center gap-4">
              {logoUrl && (
                <img src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt="Company Logo" className="w-16 h-16 object-contain rounded border border-slate-200" data-testid="cp-logo-preview" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="cp-upload-logo">
                  <Upload className="w-3.5 h-3.5 mr-1" /> {logoUrl ? 'Change Logo' : 'Upload Logo'}
                </Button>
                <p className="text-[10px] text-slate-400 mt-1">PNG/JPG, max 5MB. Appears on invoices/receipts.</p>
              </div>
            </div>
          </div>
          <div>
            <label className="form-label">PDF Footer Text</label>
            <input type="text" value={profile.footer_text || ''} onChange={(e) => setProfile(p => ({ ...p, footer_text: e.target.value }))}
              className="form-input" placeholder="e.g. Thank you for your business!" data-testid="cp-footer" />
          </div>
        </div>
      </div>

      <div className="glass-card rounded-xl p-6 space-y-5">
        <h3 className="font-heading font-semibold text-gray-800 dark:text-gray-100 text-sm border-b border-purple-100 dark:border-purple-900/30 pb-2">Accounting Settings</h3>
        <div>
          <label className="form-label">Posting Mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {POSTING_MODES.map(m => (
              <button key={m.value} onClick={() => setProfile(p => ({ ...p, posting_mode: m.value }))}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  profile.posting_mode === m.value ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400' : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                }`} data-testid={`cp-posting-${m.value}`}>
                <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{m.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* GSTR Auto-Generation Toggle — independent from per-invoice Auto GST.
            This controls whether GSTR-1/GSTR-3B reports are auto-built from
            filed invoices. Individual invoice GST is now controlled inside
            the invoice form itself (per-invoice opt-in). */}
        <div className="border border-purple-100 dark:border-purple-900/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">Auto-generate GSTR Reports</p>
              <p className="text-xs text-gray-500 mt-0.5">When ON, GSTR-1 and GSTR-3B returns are compiled automatically from posted invoices. Per-invoice GST is controlled inside each invoice.</p>
            </div>
            <button onClick={async () => {
              const newVal = !gstAutoCalc;
              setGstAutoCalc(newVal);
              try {
                await api.put('/gst-returns/settings', { auto_generate: newVal });
                toast.success(newVal ? 'Auto GST calculation enabled' : 'Auto GST calculation disabled');
              } catch { toast.error('Failed to update GST setting'); setGstAutoCalc(!newVal); }
            }} data-testid="gst-auto-calc-toggle"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all">
              {gstAutoCalc
                ? <ToggleRight className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                : <ToggleLeft className="w-6 h-6 text-gray-400" />}
              <span className={`text-sm font-medium ${gstAutoCalc ? 'text-purple-700 dark:text-purple-300' : 'text-gray-400'}`}>
                {gstAutoCalc ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        </div>

        {/* Advance Payment Engine Toggle */}
        <div className="border border-purple-100 dark:border-purple-900/30 rounded-xl p-4" data-testid="advance-engine-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-purple-500" />
              <div>
                <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">Advance Payment Engine</p>
                <p className="text-xs text-gray-500 mt-0.5">When OFF: Hides advance fields from Invoices, LR, Collection Memos. No advance entries created in accounts.</p>
              </div>
            </div>
            <button onClick={async () => {
              const newVal = !advanceEngineEnabled;
              setAdvanceEngineEnabled(newVal);
              try {
                await settingsAPI.updateAdvanceEngine({ enabled: newVal });
                toast.success(newVal ? 'Advance Payment Engine enabled' : 'Advance Payment Engine disabled');
              } catch { toast.error('Failed to update setting'); setAdvanceEngineEnabled(!newVal); }
            }} data-testid="advance-engine-toggle"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all">
              {advanceEngineEnabled
                ? <ToggleRight className="w-6 h-6 text-emerald-600" />
                : <ToggleLeft className="w-6 h-6 text-gray-400" />}
              <span className={`text-sm font-medium ${advanceEngineEnabled ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-400'}`}>
                {advanceEngineEnabled ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="cp-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
    </div>
  );
}
